import React, { FC, memo, useEffect, useMemo, useState } from "react";

import { Box } from "@UI/Components/Box/Box";
import { Heading } from "@UI/Components/Heading/Heading";
import { Image } from "@UI/Components/Image/Image";
import { ResponsiveModal } from "@UI/Components/ResponsiveModal/ResponsiveModal";
import useI18n from "@I18n/hooks/useI18n";

import messages from "../../messages";

import avatarPhoto1 from "assets/images/service-request/avatar-photo-1.png";
import avatarPhoto2 from "assets/images/service-request/avatar-photo-2.png";
import avatarPhoto3 from "assets/images/service-request/avatar-photo-3.png";
import avatarPhoto4 from "assets/images/service-request/avatar-photo-4.png";
import avatarPhoto5 from "assets/images/service-request/avatar-photo-5.png";
import avatarPhoto6 from "assets/images/service-request/avatar-photo-6.png";
import avatarPhoto7 from "assets/images/service-request/avatar-photo-7.png";

const CAPTION_HOLD_MS = 2500;
const CAPTION_FADE_MS = 300;

const PULSE_DURATION = "1.4s";
const PULSE_SECONDS = 1.4;
const PULSE_SCALE_KEYFRAMES = {
  "0%": { transform: "scale(1)" },
  "50%": { transform: "scale(1.125)" },
  "100%": { transform: "scale(1)" },
};

const AVATARS = [
  avatarPhoto1,
  avatarPhoto2,
  avatarPhoto3,
  avatarPhoto4,
  avatarPhoto5,
  avatarPhoto6,
  avatarPhoto7,
];

// One shared keyframe drives the whole cycle; each stacked photo is phase-shifted
// into its own 1.4s slice (one pulse each) via a negative animation-delay, so only
// one is visible at a time. The fade edges are wider than a bare slice so consecutive
// photos overlap and cross-dissolve instead of snapping. 7 photos x 1.4s = 9.8s cycle.
const FLASH_CYCLE_SECONDS = 9.8;

const FLASH_KEYFRAMES = {
  "0%": { opacity: 0 },
  "5.71%": { opacity: 1 },
  "8.57%": { opacity: 1 },
  "20%": { opacity: 0 },
  "100%": { opacity: 0 },
};

// Two rings sharing one keyframe, each running over two breaths and started half a
// breath apart, so a ping fires every single breath but alternates pink/orange.
const PING_CYCLE_SECONDS = PULSE_SECONDS * 2;

const PING_KEYFRAMES = {
  "0%": { transform: "translate(-50%, -50%) scale(1)", opacity: 0.5 },
  "35%": { transform: "translate(-50%, -50%) scale(1.8)", opacity: 0 },
  "100%": { transform: "translate(-50%, -50%) scale(1.8)", opacity: 0 },
};

export interface IProfilePicsLoaderModalProps {
  isOpen: boolean;
  captions?: string[];
}

/** Shown while the publish PUT (`validate: true`) is in flight (RT-4321). */
export const ProfilePicsLoaderModal: FC<IProfilePicsLoaderModalProps> = memo(
  ({ isOpen, captions }) => {
    const { formatMessage } = useI18n();
    const [captionIndex, setCaptionIndex] = useState(0);
    const [captionVisible, setCaptionVisible] = useState(true);
    const resolvedCaptions = useMemo(
      () =>
        captions ?? [
          formatMessage(
            messages["taskCreation.profilePicsLoaderModal.defaultCaption"]
          ),
        ],
      [captions, formatMessage]
    );

    useEffect(() => {
      if (resolvedCaptions.length <= 1) return undefined;

      const interval = setInterval(() => {
        setCaptionVisible(false);
        setTimeout(() => {
          setCaptionIndex((index) => (index + 1) % resolvedCaptions.length);
          setCaptionVisible(true);
        }, CAPTION_FADE_MS);
      }, CAPTION_HOLD_MS + CAPTION_FADE_MS);

      return () => clearInterval(interval);
    }, [resolvedCaptions]);

    return (
      <ResponsiveModal
        isOpen={isOpen}
        onClose={() => undefined}
        closeOnOverlayClick={false}
        closeOnEsc={false}
        closeButtonDisabled
        isCentered
      >
        <Box display="flex" flexDirection="column" alignItems="center" py="10" gap="8">
          <Box
            position="relative"
            boxSize="64px"
            display="flex"
            alignItems="center"
            justifyContent="center"
          >
            <Box
              position="absolute"
              top="50%"
              left="50%"
              boxSize="64px"
              border="2px solid"
              borderColor="pink.400"
              borderRadius="full"
              sx={{
                "@keyframes profilePicsPingPink": PING_KEYFRAMES,
                animation: `profilePicsPingPink ${PING_CYCLE_SECONDS}s ease-out infinite`,
              }}
            />
            <Box
              position="absolute"
              top="50%"
              left="50%"
              boxSize="64px"
              border="2px solid"
              borderColor="orange.400"
              borderRadius="full"
              sx={{
                "@keyframes profilePicsPingOrange": PING_KEYFRAMES,
                animation: `profilePicsPingOrange ${PING_CYCLE_SECONDS}s ease-out infinite`,
                animationDelay: `-${PULSE_SECONDS}s`,
              }}
            />
            <Box
              position="relative"
              boxSize="64px"
              sx={{
                "@keyframes profilePicsBreathe": PULSE_SCALE_KEYFRAMES,
                animation: `profilePicsBreathe ${PULSE_DURATION} ease-in-out infinite`,
              }}
            >
              {AVATARS.map((avatarSrc, index) => (
                <Image
                  key={avatarSrc}
                  src={avatarSrc}
                  position="absolute"
                  inset="0"
                  boxSize="64px"
                  borderRadius="full"
                  sx={{
                    "@keyframes profilePicsFlash": FLASH_KEYFRAMES,
                    animation: `profilePicsFlash ${FLASH_CYCLE_SECONDS}s ease-in-out infinite`,
                    animationDelay: `${-(index * PULSE_SECONDS)}s`,
                  }}
                />
              ))}
            </Box>
          </Box>
          <Heading
            as="h4"
            fontSize={{ base: "xl", lg: "3xl" }}
            color="gray.800"
            textAlign="center"
            transition={`opacity ${CAPTION_FADE_MS}ms ease-in-out`}
            opacity={captionVisible ? 1 : 0}
          >
            {resolvedCaptions[captionIndex]}
          </Heading>
        </Box>
      </ResponsiveModal>
    );
  }
);

ProfilePicsLoaderModal.displayName = "ProfilePicsLoaderModal";
